import type { TranslationMessages } from 'react-admin';

/**
 * Polskie napisy react-admina.
 *
 * Bez `i18nProvider` react-admin mówi po angielsku: „ADD FILTER”, „EXPORT”,
 * „Rows per page”, „1-25 of 6678”, „No Zlecenia found using the current
 * filters” — obok polskich etykiet naszych ekranów (przeklik dev 25.09.2026).
 * Pakiet `ra-language-polish` nie nadąża za wersją 5, więc słownik jest nasz,
 * klucz w klucz z `ra-language-english` 5.15 (test pilnuje kompletu).
 *
 * Tam, gdzie angielski wstawia nazwę zasobu (`%{name}`), po polsku jej nie
 * odmieniamy — etykiety zasobów są w mianowniku liczby mnogiej („Zlecenia”),
 * więc „Brak Zlecenia” brzmiałoby gorzej niż zdanie bez nazwy.
 *
 * Liczba mnoga: polyglot dla `pl` ma trzy formy (1 / 2–4 / 5+), oddzielone
 * „||||”.
 */
export const polishMessages: TranslationMessages = {
  ra: {
    action: {
      add_filter: 'Dodaj filtr',
      add: 'Dodaj',
      back: 'Wróć',
      bulk_actions: 'Zaznaczono 1 pozycję |||| Zaznaczono %{smart_count} pozycje |||| Zaznaczono %{smart_count} pozycji',
      cancel: 'Anuluj',
      clear_array_input: 'Wyczyść listę',
      clear_input_value: 'Wyczyść',
      clone: 'Powiel',
      confirm: 'Potwierdź',
      create: 'Utwórz',
      create_item: 'Utwórz %{item}',
      delete: 'Usuń',
      edit: 'Edytuj',
      export: 'Eksportuj',
      list: 'Lista',
      refresh: 'Odśwież',
      remove_filter: 'Usuń ten filtr',
      remove_all_filters: 'Usuń wszystkie filtry',
      remove: 'Usuń',
      reset: 'Przywróć',
      save: 'Zapisz',
      search: 'Szukaj',
      search_columns: 'Szukaj kolumn',
      select_all: 'Zaznacz wszystko',
      select_all_button: 'Zaznacz wszystko',
      select_row: 'Zaznacz ten wiersz',
      show: 'Pokaż',
      sort: 'Sortuj',
      undo: 'Cofnij',
      unselect: 'Odznacz',
      expand: 'Rozwiń',
      close: 'Zamknij',
      open_menu: 'Otwórz menu',
      close_menu: 'Zamknij menu',
      update: 'Aktualizuj',
      move_up: 'Przesuń w górę',
      move_down: 'Przesuń w dół',
      open: 'Otwórz',
      toggle_theme: 'Przełącz tryb jasny/ciemny',
      select_columns: 'Kolumny',
      update_application: 'Przeładuj aplikację',
    },
    boolean: {
      true: 'Tak',
      false: 'Nie',
      null: ' ',
    },
    page: {
      create: 'Nowy wpis — %{name}',
      dashboard: 'Pulpit',
      edit: '%{name} %{recordRepresentation}',
      error: 'Coś poszło nie tak',
      list: '%{name}',
      loading: 'Wczytywanie',
      not_found: 'Nie znaleziono',
      show: '%{name} %{recordRepresentation}',
      empty: 'Brak wpisów.',
      invite: 'Chcesz dodać pierwszy wpis?',
      access_denied: 'Brak dostępu',
      authentication_error: 'Błąd logowania',
    },
    input: {
      file: {
        upload_several: 'Przeciągnij tu pliki albo kliknij, żeby je wybrać.',
        upload_single: 'Przeciągnij tu plik albo kliknij, żeby go wybrać.',
      },
      image: {
        upload_several: 'Przeciągnij tu obrazy albo kliknij, żeby je wybrać.',
        upload_single: 'Przeciągnij tu obraz albo kliknij, żeby go wybrać.',
      },
      references: {
        all_missing: 'Nie udało się wczytać powiązanych danych.',
        many_missing: 'Co najmniej jeden z powiązanych wpisów jest już niedostępny.',
        single_missing: 'Powiązany wpis jest już niedostępny.',
      },
      password: {
        toggle_visible: 'Ukryj hasło',
        toggle_hidden: 'Pokaż hasło',
      },
    },
    message: {
      about: 'Informacje',
      access_denied: 'Nie masz uprawnień do tej strony',
      are_you_sure: 'Czy na pewno?',
      authentication_error: 'Nie udało się sprawdzić danych logowania. Spróbuj ponownie za chwilę.',
      auth_error: 'Nie udało się potwierdzić logowania. Zaloguj się ponownie.',
      bulk_delete_content:
        'Czy na pewno usunąć ten wpis? |||| Czy na pewno usunąć te %{smart_count} wpisy? |||| Czy na pewno usunąć tych %{smart_count} wpisów?',
      bulk_delete_title: 'Usuń wpis |||| Usuń %{smart_count} wpisy |||| Usuń %{smart_count} wpisów',
      bulk_update_content:
        'Czy na pewno zaktualizować ten wpis? |||| Czy na pewno zaktualizować te %{smart_count} wpisy? |||| Czy na pewno zaktualizować tych %{smart_count} wpisów?',
      bulk_update_title:
        'Aktualizuj wpis |||| Aktualizuj %{smart_count} wpisy |||| Aktualizuj %{smart_count} wpisów',
      clear_array_input: 'Czy na pewno wyczyścić całą listę?',
      delete_content: 'Czy na pewno usunąć ten wpis?',
      delete_title: 'Usuń %{recordRepresentation}',
      details: 'Szczegóły',
      error: 'Wystąpił błąd aplikacji. Odśwież stronę i spróbuj ponownie.',
      invalid_form: 'Formularz zawiera błędy. Sprawdź zaznaczone pola.',
      loading: 'Proszę czekać',
      no: 'Nie',
      not_found: 'Błędny adres albo nieaktualny link.',
      select_all_limit_reached: 'Za dużo pozycji, żeby zaznaczyć wszystkie. Zaznaczono pierwsze %{max}.',
      unsaved_changes: 'Część zmian nie została zapisana. Czy na pewno chcesz je porzucić?',
      yes: 'Tak',
      placeholder_data_warning: 'Nie udało się odświeżyć danych. Sprawdź połączenie z siecią.',
    },
    navigation: {
      clear_filters: 'Wyczyść filtry',
      no_filtered_results: 'Brak wyników dla wybranych filtrów.',
      no_results: 'Brak wyników',
      no_more_results: 'Nie ma strony %{page}. Wróć do poprzedniej.',
      page_out_of_boundaries: 'Nie ma strony %{page}',
      page_out_from_end: 'To ostatnia strona',
      page_out_from_begin: 'To pierwsza strona',
      page_range_info: '%{offsetBegin}–%{offsetEnd} z %{total}',
      partial_page_range_info: '%{offsetBegin}–%{offsetEnd} z ponad %{offsetEnd}',
      current_page: 'Strona %{page}',
      page: 'Przejdź do strony %{page}',
      first: 'Pierwsza strona',
      last: 'Ostatnia strona',
      next: 'Następna strona',
      previous: 'Poprzednia strona',
      page_rows_per_page: 'Wierszy na stronie:',
      skip_nav: 'Przejdź do treści',
    },
    sort: {
      sort_by: 'Sortuj: %{field_lower_first} %{order}',
      ASC: 'rosnąco',
      DESC: 'malejąco',
    },
    auth: {
      auth_check_error: 'Zaloguj się, żeby kontynuować',
      user_menu: 'Profil',
      // Logujemy adresem e-mail (API szuka konta po `email`) — „Nazwa
      // użytkownika” na ekranie logowania myliła (07.10.2026). Klucz
      // `ra.auth.username` czyta wyłącznie formularz logowania.
      username: 'E-mail',
      password: 'Hasło',
      email: 'E-mail',
      sign_in: 'Zaloguj się',
      sign_in_error: 'Nie udało się zalogować. Spróbuj ponownie.',
      logout: 'Wyloguj',
    },
    notification: {
      updated: 'Zapisano zmiany |||| Zapisano %{smart_count} wpisy |||| Zapisano %{smart_count} wpisów',
      created: 'Utworzono wpis',
      deleted: 'Usunięto wpis |||| Usunięto %{smart_count} wpisy |||| Usunięto %{smart_count} wpisów',
      bad_item: 'Nieprawidłowy wpis',
      item_doesnt_exist: 'Wpis nie istnieje',
      http_error: 'Błąd komunikacji z serwerem. Spróbuj ponownie za chwilę.',
      data_provider_error: 'Nie udało się pobrać danych. Odśwież stronę, a jeśli błąd się powtórzy, zgłoś go.',
      i18n_error: 'Nie udało się wczytać tłumaczeń dla wybranego języka',
      canceled: 'Anulowano',
      logged_out: 'Sesja wygasła. Zaloguj się ponownie.',
      not_authorized: 'Nie masz uprawnień do tych danych.',
      application_update_available: 'Dostępna jest nowa wersja.',
      offline: 'Brak połączenia. Nie udało się pobrać danych.',
    },
    validation: {
      required: 'Wymagane',
      minLength: 'Co najmniej %{min} znaków',
      maxLength: 'Najwyżej %{max} znaków',
      minValue: 'Co najmniej %{min}',
      maxValue: 'Najwyżej %{max}',
      number: 'Musi być liczbą',
      email: 'Nieprawidłowy adres e-mail',
      oneOf: 'Dozwolone wartości: %{options}',
      regex: 'Nieprawidłowy format. Wymagany wzorzec: %{pattern}',
      unique: 'Taka wartość już istnieje',
    },
    // „Zapisane filtry”, nie „zapytania” (poprawa brzmienia 01.10.2026):
    // „zapytanie” to kalka „query”, a w tej aplikacji zapytanie to wiadomość
    // do przewoźnika.
    saved_queries: {
      label: 'Zapisane filtry',
      query_name: 'Nazwa zestawu filtrów',
      new_label: 'Zapisz obecne filtry…',
      new_dialog_title: 'Zapisz obecne filtry jako',
      remove_label: 'Usuń zapisane filtry',
      remove_label_with_name: 'Usuń zapisane filtry „%{name}”',
      remove_dialog_title: 'Usunąć zapisane filtry?',
      remove_message: 'Czy na pewno usunąć ten zestaw filtrów z listy zapisanych?',
      help: 'Ustaw filtry i zapisz je na później',
    },
    guesser: {
      empty: {
        title: 'Brak danych',
        message: 'Sprawdź źródło danych',
      },
    },
    configurable: {
      customize: 'Dostosuj',
      configureMode: 'Dostosuj tę stronę',
      inspector: {
        title: 'Inspektor',
        content: 'Najedź na element interfejsu, żeby go dostosować',
        reset: 'Przywróć ustawienia',
        hideAll: 'Ukryj wszystko',
        showAll: 'Pokaż wszystko',
      },
      Datagrid: {
        title: 'Tabela',
        unlabeled: 'Kolumna bez nazwy #%{column}',
      },
      SimpleForm: {
        title: 'Formularz',
        unlabeled: 'Pole bez nazwy #%{input}',
      },
      SimpleList: {
        title: 'Lista',
        primaryText: 'Tekst główny',
        secondaryText: 'Tekst pomocniczy',
        tertiaryText: 'Tekst dodatkowy',
      },
    },
  },
};
